/**
 * PaymentModal Component
 *
 * Dialog for recording a payment against an invoice.
 *
 * @module components/invoices/PaymentModal
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import { formatCurrency } from '../../utils/formatters';

const PAYMENT_METHODS = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'credit_card', label: 'Tarjeta de Credito' },
  { value: 'other', label: 'Otro' },
];

// `currency` sin default a proposito (#617): sale de `invoices.currency` o no
// se rotula. Un default aca volveria a poner un simbolo inventado sobre el
// saldo que el usuario esta por cobrar.
const PaymentModal = ({ show, onHide, onSave, balance = 0, currency, loading = false }) => {
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'transfer',
    reference_number: '',
    notes: '',
  });

  useEffect(() => {
    if (show) {
      setFormData({
        amount: balance > 0 ? balance.toFixed(2) : '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'transfer',
        reference_number: '',
        notes: '',
      });
    }
  }, [show, balance]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Registrar Pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {balance > 0 && (
            <div className="bg-light p-3 rounded mb-3">
              <small className="text-muted">Saldo Pendiente:</small>
              <div className="fw-bold text-primary fs-5">{formatCurrency(balance, currency)}</div>
            </div>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Monto *</Form.Label>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  required
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Fecha de Pago *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => handleChange('payment_date', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Metodo de Pago</Form.Label>
                <Form.Select
                  value={formData.payment_method}
                  onChange={(e) => handleChange('payment_method', e.target.value)}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Numero de Referencia</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.reference_number}
                  onChange={(e) => handleChange('reference_number', e.target.value)}
                  placeholder="Ej: TRF-001234"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Notas adicionales..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="success"
            disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Registrando...
              </>
            ) : (
              <>
                <i className="bi bi-cash me-1"></i>
                Registrar Pago
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentModal;
